import type { ReactNode } from "react";
import { cn } from "@/lib/cn";

/**
 * Single source of truth for status indicators across the app.
 *
 * Three variants:
 *   - dot: 6px filled circle + label, for inline use in tables/rows
 *   - chip: bordered pill with low-saturation surface, for header positions
 *   - text: just the colored label, for tight contexts
 *
 * All tones are pulled from the semantic token set (good/warn/bad/info/
 * accent/neutral). Never hand-code a status color in a page — extend
 * this component instead.
 */

export type StatusTone =
  | "good"
  | "warn"
  | "bad"
  | "info"
  | "accent"
  | "neutral";

export type StatusVariant = "dot" | "chip" | "text";

interface Props {
  tone?: StatusTone;
  variant?: StatusVariant;
  /** Adds the slow pulse to the dot for "in-progress" states. */
  pulse?: boolean;
  /** Override the dot/chip text. If omitted, just the indicator renders. */
  children?: ReactNode;
  className?: string;
}

const dotColor: Record<StatusTone, string> = {
  good: "bg-good",
  warn: "bg-warn",
  bad: "bg-bad",
  info: "bg-info",
  accent: "bg-accent",
  neutral: "bg-foreground/35",
};

const textColor: Record<StatusTone, string> = {
  good: "text-good",
  warn: "text-warn",
  bad: "text-bad",
  info: "text-info",
  accent: "text-accent",
  neutral: "text-foreground/55",
};

const chipBorder: Record<StatusTone, string> = {
  good: "border-good/40 bg-good/10 text-good",
  warn: "border-warn/40 bg-warn/10 text-warn",
  bad: "border-bad/40 bg-bad/10 text-bad",
  info: "border-info/40 bg-info/10 text-info",
  accent: "border-accent/40 bg-accent/10 text-accent",
  neutral: "border-line bg-surface-2 text-foreground/65",
};

export function Status({
  tone = "neutral",
  variant = "dot",
  pulse = false,
  children,
  className,
}: Props) {
  if (variant === "chip") {
    return (
      <span
        className={cn(
          "inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-[0.12em]",
          chipBorder[tone],
          className,
        )}
      >
        <span
          className={cn(
            "h-1.5 w-1.5 rounded-full",
            dotColor[tone],
            pulse && "animate-dot-pulse",
          )}
          aria-hidden
        />
        {children}
      </span>
    );
  }

  if (variant === "text") {
    return (
      <span className={cn("font-medium", textColor[tone], className)}>
        {children}
      </span>
    );
  }

  // default: dot variant
  return (
    <span
      className={cn(
        "inline-flex items-center gap-2 text-xs",
        textColor[tone],
        className,
      )}
    >
      <span
        className={cn(
          "h-1.5 w-1.5 shrink-0 rounded-full",
          dotColor[tone],
          pulse && "animate-dot-pulse",
        )}
        aria-hidden
      />
      {children}
    </span>
  );
}
